// GAVION: reassign a task (« Assignée à »). An admin reassigns any task; a rep reassigns the tasks he owns, to anyone.
// Server side the tasks update policy only asks rep-or-admin (and deal visibility), so this rule is a UI rule.
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetIdentity,
  useGetList,
  useNotify,
  usePermissions,
  useTranslate,
  useUpdate,
} from "ra-core";
import {
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import type { Task } from "../types";

type SaleChoice = {
  id: number | string;
  first_name?: string | null;
  last_name?: string | null;
};

export const saleName = (s?: Partial<SaleChoice> | null) =>
  s ? `${s.first_name ?? ""} ${s.last_name ?? ""}`.trim() : "";

export const SALES_SORT = { field: "last_name", order: "ASC" } as const;
export const ACTIVE_SALES_FILTER = { "disabled@neq": true };
/** Page size for the user pickers (reassign menu and the form's « Assignée à »): every active user, not 25. */
export const SALES_PER_PAGE = 200;

/** Admin: any task. Rep: only the tasks he owns (task.sales_id = him). A task not yet saved (create) is his own. */
export const canReassignTask = (
  isAdmin: boolean,
  identityId: unknown,
  task?: Pick<Task, "id" | "sales_id"> | null,
): boolean => {
  if (isAdmin) return true;
  if (identityId == null) return false;
  if (!task || task.id == null) return true;
  return task.sales_id != null && String(task.sales_id) === String(identityId);
};

export const useCanReassignTask = (task?: Task | null): boolean => {
  const { permissions } = usePermissions();
  const { identity } = useGetIdentity();
  return canReassignTask(permissions === "admin", identity?.id, task);
};

const ReassignChoices = ({
  task,
  onReassign,
}: {
  task: Task;
  onReassign: (sale: SaleChoice) => void;
}) => {
  const translate = useTranslate();
  const { data = [] } = useGetList<SaleChoice>("sales", {
    filter: ACTIVE_SALES_FILTER,
    sort: SALES_SORT,
    pagination: { page: 1, perPage: SALES_PER_PAGE },
  });
  return (
    <>
      {data.map((sale) => {
        const current = String(sale.id) === String(task.sales_id);
        return (
          <DropdownMenuItem
            key={sale.id}
            className="cursor-pointer h-12 md:h-8 px-4 md:px-2 text-base md:text-sm"
            onClick={() => {
              if (!current) onReassign(sale);
            }}
          >
            {saleName(sale)}
            {current ? ` (${translate("gavion.tasks.assigned_current")})` : ""}
          </DropdownMenuItem>
        );
      })}
    </>
  );
};

/** Updates sales_id, tells the user and refreshes the task lists. Lives in the row (the menu unmounts on click). */
export const useReassignTask = (task: Task) => {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [update] = useUpdate();
  return (sale: SaleChoice) =>
    update(
      "tasks",
      { id: task.id, data: { sales_id: sale.id }, previousData: task },
      {
        onSuccess: () => {
          notify("gavion.tasks.reassigned", {
            messageArgs: { name: sale.first_name || saleName(sale) },
          });
          queryClient.invalidateQueries({ queryKey: ["tasks"] });
        },
      },
    );
};

/** « Réassigner » submenu of the task row's « … » menu; nothing when the user may not reassign this task. */
export const ReassignMenu = ({
  task,
  onReassign,
}: {
  task: Task;
  onReassign: (sale: SaleChoice) => void;
}) => {
  const translate = useTranslate();
  const can = useCanReassignTask(task);
  if (!can) return null;
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger className="cursor-pointer h-12 md:h-8 px-4 md:px-2 text-base md:text-sm">
        {translate("gavion.tasks.reassign")}
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent>
        <ReassignChoices task={task} onReassign={onReassign} />
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
};
